import { format, parseISO } from 'date-fns';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  ArrowUp,
  Collapse,
  FlexRow,
  Header,
  Icon,
  Text,
} from '@summer/ui-components';

import { Box } from 'components/box/Box';
import NoDataToDisplay from 'components/placeholders/NoDataToDisplay';
import {
  fetchFileDownloadUrl,
  useBorrowerFiles,
  type BorrowerFile,
} from 'state/server/files';

const PER_PAGE = 10;

const BorrowerFiles = () => {
  const { borrowerId } = useParams() as { borrowerId: string };
  const [page, setPage] = useState(1);

  const { data, isPending } = useBorrowerFiles(borrowerId, page, PER_PAGE);

  const files = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PER_PAGE);
  const showingStart = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const showingEnd = Math.min(page * PER_PAGE, total);

  const handleDownload = useCallback(
    async (fileId: string) => {
      if (!borrowerId) return;
      try {
        const result = await fetchFileDownloadUrl({
          borrowerId,
          fileId,
        });
        window.open(result.downloadUrl, '_blank');
      } catch {
        // Download failed silently
      }
    },
    [borrowerId],
  );

  return (
    <Box mt={40}>
      <Collapse
        openByDefault={true}
        trigger={({ openState }: { openState: boolean }) => (
          <CollapseTrigger>
            <Header
              {...({} as Record<string, unknown>)}
              // @ts-expect-error size overrides the computed font-size and weight is inert — see [sc-52767]
              size="large"
              weight="bold"
            >
              Uploaded Files
            </Header>
            <Arrow
              {...({} as Record<string, unknown>)}
              fill="#6D7581"
              open={openState}
              SvgComponent={ArrowUp}
            />
            {total > 0 && (
              <FileCountText size="small">
                {total} file{total !== 1 ? 's' : ''} on this account
              </FileCountText>
            )}
          </CollapseTrigger>
        )}
      >
        {isPending && (
          <LoadingContainer>
            <Text>Loading files...</Text>
          </LoadingContainer>
        )}
        {!isPending && files.length === 0 && <NoDataToDisplay />}
        {!isPending && files.length > 0 && (
          <Box
            bg="white"
            border="solid 1px"
            borderRadius={6}
            borderColor="blueGrey"
            px={24}
            pt={24}
            mt={24}
          >
            <TableHeader>
              <HeaderCell flex={3}>File name</HeaderCell>
              <HeaderCell flex={2}>Uploaded</HeaderCell>
              <HeaderCell flex={1}>Actions</HeaderCell>
            </TableHeader>
            {files.map((file: BorrowerFile) => (
              <TableRow key={file.id}>
                <TableCell flex={3}>
                  <FileName>{file.name}</FileName>
                  <FileMetadata>
                    {file.contentType}

                    {file.sizeBytes !== null
                      ? ` · ${formatFileSize(file.sizeBytes)}`
                      : ''}
                  </FileMetadata>
                </TableCell>
                <TableCell flex={2}>
                  <Text>
                    {format(parseISO(file.uploadedAt), 'dd MMM yyyy')}
                  </Text>
                </TableCell>
                <TableCell flex={1}>
                  <DownloadLink onClick={() => handleDownload(file.id)}>
                    &#8595; Download
                  </DownloadLink>
                </TableCell>
              </TableRow>
            ))}
            {total > 0 && (
              <PaginationContainer>
                <Text>
                  Showing {showingStart}-{showingEnd} of {total} uploads
                </Text>
                {totalPages > 1 && (
                  <PaginationButtons>
                    <PageButton
                      disabled={page <= 1}
                      onClick={() => {
                        setPage(p => p - 1);
                      }}
                    >
                      Previous
                    </PageButton>
                    <PageButton
                      disabled={page >= totalPages}
                      onClick={() => {
                        setPage(p => p + 1);
                      }}
                    >
                      Next
                    </PageButton>
                  </PaginationButtons>
                )}
              </PaginationContainer>
            )}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CollapseTrigger = styled(FlexRow)`
  width: 100%;
  cursor: pointer;
  padding: 16px 0;
  margin-bottom: 24px;
`;

const Arrow = styled(Icon)<{ open: boolean }>`
  cursor: pointer;
  margin-left: 8px;
  transform: ${(props: { open: boolean }) =>
    props.open ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease-out;
`;

const FileCountText = styled(Text).attrs({} as Record<string, unknown>)`
  margin-left: 16px;
  align-self: center;
  color: #6d7581;
`;

const LoadingContainer = styled.div`
  padding: 24px;
  text-align: center;
`;

const TableHeader = styled(FlexRow)`
  border-bottom: 2px solid #e8eaed;
  padding-bottom: 12px;
  margin-bottom: 8px;
`;

const HeaderCell = styled.div<{ flex: number }>`
  flex: ${props => props.flex};
  font-weight: 600;
  font-size: 14px;
  color: #6d7581;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled(FlexRow)`
  padding: 16px 0;
  border-bottom: 1px solid #e8eaed;
  align-items: center;

  &:last-of-type {
    border-bottom: none;
  }
`;

const TableCell = styled.div<{ flex: number }>`
  flex: ${props => props.flex};
`;

const FileName = styled(Text).attrs({} as Record<string, unknown>)`
  font-weight: 600;
  overflow-wrap: break-word;
  word-break: break-all;
`;

const FileMetadata = styled(Text).attrs({} as Record<string, unknown>)`
  font-size: 12px;
  color: #6d7581;
  margin-top: 2px;
`;

const DownloadLink = styled.button`
  background: none;
  border: none;
  color: #0098eb;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-size: 14px;

  &:hover {
    opacity: 0.8;
  }
`;

const PaginationContainer = styled(FlexRow)`
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-top: 1px solid #e8eaed;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const PageButton = styled.button<{ disabled?: boolean }>`
  background: none;
  border: 1px solid #e8eaed;
  border-radius: 4px;
  padding: 6px 16px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  font-size: 14px;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }
`;

export default BorrowerFiles;
